import React, { useState } from 'react';
import {
  Box, Typography, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Chip, Button, TextField, IconButton,
  Tabs, Tab, Grid, Card, CardContent, Divider, FormControl,
  InputLabel, Select, MenuItem, InputAdornment, Snackbar, Alert
} from '@mui/material';
import { Payment, AttachMoney, Assessment, Add, Edit, Search, Download, TrendingUp, TrendingDown } from '@mui/icons-material';
import { useLocation } from 'react-router-dom';

const tuitionRates = [
  { id: 1, grade: 'Kindergarten', annual: 8500, monthly: 850, registration: 250, bookFee: 150 },
  { id: 2, grade: '1st – 2nd Grade', annual: 9000, monthly: 900, registration: 250, bookFee: 175 },
  { id: 3, grade: '3rd – 5th Grade', annual: 9500, monthly: 950, registration: 250, bookFee: 200 },
  { id: 4, grade: '6th – 8th Grade', annual: 10500, monthly: 1050, registration: 300, bookFee: 225 },
  { id: 5, grade: '9th – 12th Grade', annual: 11500, monthly: 1150, registration: 300, bookFee: 275 },
];

const mockPayments = [
  { id: 'PAY-001', student: 'Emma Johnson', parent: 'Sarah Johnson', amount: 950, type: 'Monthly Tuition', method: 'Credit Card', date: '2024-01-10', status: 'paid' },
  { id: 'PAY-002', student: 'Liam Parker', parent: 'James Parker', amount: 950, type: 'Monthly Tuition', method: 'Bank Transfer', date: '2024-01-12', status: 'paid' },
  { id: 'PAY-003', student: 'Sophia Martinez', parent: 'Rosa Martinez', amount: 1050, type: 'Monthly Tuition', method: 'Check', date: '2024-01-08', status: 'paid' },
  { id: 'PAY-004', student: 'Noah Thompson', parent: 'Karen Thompson', amount: 950, type: 'Monthly Tuition', method: 'Credit Card', date: '2024-01-15', status: 'overdue' },
  { id: 'PAY-005', student: 'Ava Garcia', parent: 'Carlos Garcia', amount: 1050, type: 'Monthly Tuition', method: 'Bank Transfer', date: '2024-01-09', status: 'paid' },
  { id: 'PAY-006', student: 'Mason Wilson', parent: 'Linda Wilson', amount: 250, type: 'Registration Fee', method: 'Credit Card', date: '2024-01-06', status: 'paid' },
  { id: 'PAY-007', student: 'Isabella Anderson', parent: 'Robert Anderson', amount: 1050, type: 'Monthly Tuition', method: 'Credit Card', date: '2024-01-11', status: 'paid' },
  { id: 'PAY-008', student: 'James Thomas', parent: 'Patricia Thomas', amount: 1150, type: 'Monthly Tuition', method: 'Bank Transfer', date: '2024-01-07', status: 'pending' },
  { id: 'PAY-009', student: 'Mia Jackson', parent: 'Barbara Jackson', amount: 950, type: 'Monthly Tuition', method: 'Credit Card', date: '2024-01-13', status: 'paid' },
  { id: 'PAY-010', student: 'Ethan White', parent: 'Joseph White', amount: 1050, type: 'Monthly Tuition', method: 'Check', date: '2023-12-10', status: 'overdue' },
];

const statusConfig = {
  paid: { label: 'Paid', color: 'success' },
  pending: { label: 'Pending', color: 'warning' },
  overdue: { label: 'Overdue', color: 'error' },
  refunded: { label: 'Refunded', color: 'info' },
};

const FinanceAdmin = () => {
  const location = useLocation();
  const getInitialTab = () => {
    if (location.pathname.includes('/tuition')) return 0;
    if (location.pathname.includes('/payments')) return 1;
    if (location.pathname.includes('/reports')) return 2;
    return 0;
  };

  const [tab, setTab] = useState(getInitialTab());
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [snackbar, setSnackbar] = useState({ open: false, message: '' });

  const filteredPayments = mockPayments.filter(p => {
    const sm = !search || `${p.student} ${p.parent} ${p.id}`.toLowerCase().includes(search.toLowerCase());
    const fm = statusFilter === 'all' || p.status === statusFilter;
    return sm && fm;
  });

  const totalCollected = mockPayments.filter(p => p.status === 'paid').reduce((sum, p) => sum + p.amount, 0);
  const totalOverdue = mockPayments.filter(p => p.status === 'overdue').reduce((sum, p) => sum + p.amount, 0);
  const totalPending = mockPayments.filter(p => p.status === 'pending').reduce((sum, p) => sum + p.amount, 0);

  const fmt = (n) => n.toLocaleString('en-US', { style: 'currency', currency: 'USD' });

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700 }}>Financial Management</Typography>
          <Typography variant="body2" color="text.secondary">Tuition rates, payments, and financial reports</Typography>
        </Box>
        <Button variant="outlined" startIcon={<Download />}>Export Report</Button>
      </Box>

      {/* Summary Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {[
          { label: 'Collected This Month', value: fmt(totalCollected), color: 'success.main', icon: <TrendingUp />, sub: `${mockPayments.filter(p => p.status === 'paid').length} payments` },
          { label: 'Overdue', value: fmt(totalOverdue), color: 'error.main', icon: <TrendingDown />, sub: `${mockPayments.filter(p => p.status === 'overdue').length} accounts` },
          { label: 'Pending', value: fmt(totalPending), color: 'warning.main', icon: <Payment />, sub: `${mockPayments.filter(p => p.status === 'pending').length} payments` },
          { label: 'Annual Revenue (Est.)', value: fmt(totalCollected * 12), color: 'primary.main', icon: <AttachMoney />, sub: 'Projected' },
        ].map((s, i) => (
          <Grid item xs={6} md={3} key={i}>
            <Card elevation={1} sx={{ borderRadius: 2 }}>
              <CardContent sx={{ py: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <Box sx={{ color: s.color }}>{s.icon}</Box>
                  <Typography variant="body2" color="text.secondary">{s.label}</Typography>
                </Box>
                <Typography variant="h5" sx={{ fontWeight: 700, color: s.color }}>{s.value}</Typography>
                <Typography variant="caption" color="text.secondary">{s.sub}</Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Paper elevation={2} sx={{ borderRadius: 2 }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={tab} onChange={(e, v) => setTab(v)}>
            <Tab icon={<AttachMoney />} label="Tuition & Fees" iconPosition="start" />
            <Tab icon={<Payment />} label="Payment History" iconPosition="start" />
            <Tab icon={<Assessment />} label="Reports" iconPosition="start" />
          </Tabs>
        </Box>

        {/* Tuition & Fees Tab */}
        {tab === 0 && (
          <Box sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>Tuition Rate Schedule — 2023-24</Typography>
              <Button variant="outlined" size="small" startIcon={<Edit />}>Edit Rates</Button>
            </Box>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow sx={{ bgcolor: 'grey.50' }}>
                    <TableCell sx={{ fontWeight: 600 }}>Grade Level</TableCell>
                    <TableCell sx={{ fontWeight: 600 }} align="right">Annual Tuition</TableCell>
                    <TableCell sx={{ fontWeight: 600 }} align="right">Monthly</TableCell>
                    <TableCell sx={{ fontWeight: 600 }} align="right">Registration Fee</TableCell>
                    <TableCell sx={{ fontWeight: 600 }} align="right">Book / Activity Fee</TableCell>
                    <TableCell sx={{ fontWeight: 600 }} align="right">Total First Year</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {tuitionRates.map(rate => (
                    <TableRow key={rate.id} hover>
                      <TableCell sx={{ fontWeight: 500 }}>{rate.grade}</TableCell>
                      <TableCell align="right">{fmt(rate.annual)}</TableCell>
                      <TableCell align="right">{fmt(rate.monthly)}</TableCell>
                      <TableCell align="right">{fmt(rate.registration)}</TableCell>
                      <TableCell align="right">{fmt(rate.bookFee)}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>{fmt(rate.annual + rate.registration + rate.bookFee)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            <Box sx={{ mt: 4 }}>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>Additional Fees</Typography>
              <Grid container spacing={2}>
                {[
                  { label: 'After-School Care (Monthly)', value: '$350' },
                  { label: 'Before-School Care (Monthly)', value: '$200' },
                  { label: 'Lunch Program (Monthly)', value: '$180' },
                  { label: 'Uniforms (Estimated)', value: '$150 – $300' },
                  { label: 'Transportation (Per Semester)', value: '$400' },
                  { label: 'Technology Fee (Annual)', value: '$100' },
                ].map((fee, i) => (
                  <Grid item xs={12} sm={6} md={4} key={i}>
                    <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                      <Typography variant="body2" color="text.secondary">{fee.label}</Typography>
                      <Typography variant="body1" sx={{ fontWeight: 600 }}>{fee.value}</Typography>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </Box>
          </Box>
        )}

        {/* Payment History Tab */}
        {tab === 1 && (
          <Box>
            <Box sx={{ p: 2, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <TextField
                placeholder="Search payments..."
                value={search} onChange={e => setSearch(e.target.value)}
                size="small" sx={{ minWidth: 260 }}
                InputProps={{ startAdornment: <InputAdornment position="start"><Search fontSize="small" /></InputAdornment> }}
              />
              <FormControl size="small" sx={{ minWidth: 150 }}>
                <InputLabel>Status</InputLabel>
                <Select value={statusFilter} label="Status" onChange={e => setStatusFilter(e.target.value)}>
                  <MenuItem value="all">All Statuses</MenuItem>
                  <MenuItem value="paid">Paid</MenuItem>
                  <MenuItem value="pending">Pending</MenuItem>
                  <MenuItem value="overdue">Overdue</MenuItem>
                </Select>
              </FormControl>
            </Box>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow sx={{ bgcolor: 'grey.50' }}>
                    <TableCell sx={{ fontWeight: 600 }}>ID</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Student</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Parent</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Type</TableCell>
                    <TableCell sx={{ fontWeight: 600 }} align="right">Amount</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Method</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Date</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredPayments.map(p => (
                    <TableRow key={p.id} hover>
                      <TableCell><Typography variant="caption" color="text.secondary">{p.id}</Typography></TableCell>
                      <TableCell sx={{ fontWeight: 500 }}>{p.student}</TableCell>
                      <TableCell>{p.parent}</TableCell>
                      <TableCell>{p.type}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>{fmt(p.amount)}</TableCell>
                      <TableCell>{p.method}</TableCell>
                      <TableCell>{p.date}</TableCell>
                      <TableCell><Chip label={statusConfig[p.status].label} color={statusConfig[p.status].color} size="small" /></TableCell>
                      <TableCell>
                        {p.status === 'overdue' && (
                          <Button size="small" variant="outlined" color="warning" onClick={() => setSnackbar({ open: true, message: `Reminder sent to ${p.parent}` })}>
                            Send Reminder
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}

        {/* Reports Tab */}
        {tab === 2 && (
          <Box sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>Financial Reports</Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Card elevation={1} sx={{ borderRadius: 2 }}>
                  <CardContent>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2 }}>Revenue by Grade Level</Typography>
                    {tuitionRates.map((rate, i) => {
                      const studentCount = 2 - (i % 2);
                      const revenue = rate.monthly * studentCount;
                      const maxRevenue = tuitionRates[tuitionRates.length - 1].monthly * 2;
                      return (
                        <Box key={rate.id} sx={{ mb: 1.5 }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                            <Typography variant="body2">{rate.grade}</Typography>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>{fmt(revenue)}</Typography>
                          </Box>
                          <Box sx={{ height: 8, bgcolor: 'grey.200', borderRadius: 4, overflow: 'hidden' }}>
                            <Box sx={{ height: '100%', width: `${(revenue / maxRevenue) * 100}%`, bgcolor: 'primary.main', borderRadius: 4 }} />
                          </Box>
                        </Box>
                      );
                    })}
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} md={6}>
                <Card elevation={1} sx={{ borderRadius: 2 }}>
                  <CardContent>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2 }}>Payment Status Breakdown</Typography>
                    {[
                      { label: 'Paid', count: mockPayments.filter(p => p.status === 'paid').length, color: '#4caf50' },
                      { label: 'Pending', count: mockPayments.filter(p => p.status === 'pending').length, color: '#ff9800' },
                      { label: 'Overdue', count: mockPayments.filter(p => p.status === 'overdue').length, color: '#f44336' },
                    ].map(s => (
                      <Box key={s.label} sx={{ mb: 1.5 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                          <Typography variant="body2">{s.label}</Typography>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>{s.count} payments</Typography>
                        </Box>
                        <Box sx={{ height: 8, bgcolor: 'grey.200', borderRadius: 4, overflow: 'hidden' }}>
                          <Box sx={{ height: '100%', width: `${(s.count / mockPayments.length) * 100}%`, bgcolor: s.color, borderRadius: 4 }} />
                        </Box>
                      </Box>
                    ))}
                    <Divider sx={{ my: 2 }} />
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2" color="text.secondary">Total Payments</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>{mockPayments.length}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
                      <Typography variant="body2" color="text.secondary">Total Collected</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 700, color: 'success.main' }}>{fmt(totalCollected)}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
                      <Typography variant="body2" color="text.secondary">Outstanding</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 700, color: 'error.main' }}>{fmt(totalOverdue + totalPending)}</Typography>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                  {['Monthly Revenue Report', 'Annual Tuition Summary', 'Outstanding Balances', 'Payment Method Breakdown'].map(r => (
                    <Button key={r} variant="outlined" startIcon={<Download />} onClick={() => setSnackbar({ open: true, message: `${r} downloaded` })}>
                      {r}
                    </Button>
                  ))}
                </Box>
              </Grid>
            </Grid>
          </Box>
        )}
      </Paper>

      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar(p => ({ ...p, open: false }))} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert severity="success" onClose={() => setSnackbar(p => ({ ...p, open: false }))}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
};

export default FinanceAdmin;
